import { useCallback, useEffect, useRef, useState } from 'react';

export type UseTypewriterRevealConfig = {
  /** Whether the animation is enabled. If false, shows full text immediately. */
  enabled?: boolean;
};

export type UseTypewriterRevealReturn = {
  /** The currently displayed text (partial during animation, full when complete). */
  displayedText: string;
  /** Whether the reveal animation has completed. */
  isComplete: boolean;
  /** Reset the animation to start from the beginning. */
  reset: () => void;
};

type SpeedPhase = {
  label: string;
  until: number;
  min: number;
  max: number;
};

const SPEED_PROFILE: SpeedPhase[] = [
  { label: 'Warm up', until: 0.18, min: 3, max: 5 },
  { label: 'Careful', until: 0.38, min: 4, max: 8 },
  { label: 'Flow', until: 0.66, min: 2, max: 5 },
  { label: 'Reflect', until: 0.82, min: 3, max: 7 },
  { label: 'Wrap', until: 1, min: 2, max: 4 },
];
const SPEED_MULTIPLIER = 0.5;

const getPhase = (progress: number): SpeedPhase => {
  const phase = SPEED_PROFILE.find((segment) => progress <= segment.until);
  return phase ?? SPEED_PROFILE[SPEED_PROFILE.length - 1];
};

const getCharDelay = (char: string, index: number, total: number): number => {
  const progress = index / Math.max(1, total - 1);
  const phase = getPhase(progress);

  // Wave-based variation for natural feel
  const wave = (Math.sin(index / 6) + 1) / 2;
  let delay = phase.min + (phase.max - phase.min) * (0.35 + 0.65 * wave);

  // Spaces are faster
  if (char === ' ') {
    delay *= 0.7;
  }

  // Sentence-ending punctuation gets longer pause
  if ('.!?'.includes(char)) {
    delay += 13 + Math.random() * 13;
  }

  // Mid-sentence punctuation gets shorter pause
  if (',;:'.includes(char)) {
    delay += 5 + Math.random() * 8;
  }

  // Newlines get a pause
  if (char === '\n') {
    delay += 10 + Math.random() * 10;
  }

  // Small random jitter
  delay += Math.random() * 1;

  return delay * SPEED_MULTIPLIER;
};

/**
 * Hook that reveals text character by character with a typing animation effect.
 * Designed to work smoothly with streaming text - maintains consistent animation
 * speed regardless of how fast new text arrives.
 */
export function useTypewriterReveal(
  fullText: string,
  config?: UseTypewriterRevealConfig,
): UseTypewriterRevealReturn {
  const enabled = config?.enabled ?? true;

  const [charIndex, setCharIndex] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fullTextRef = useRef(fullText);
  const prevTextRef = useRef(fullText);
  const isRunningRef = useRef(false);
  const charIndexRef = useRef(0);

  // Keep refs in sync without triggering re-renders
  fullTextRef.current = fullText;
  charIndexRef.current = charIndex;

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isRunningRef.current = false;
    },
    [],
  );

  // Handle text changes - only reset if text fundamentally changed (not extended)
  useEffect(() => {
    const prevText = prevTextRef.current;
    prevTextRef.current = fullText;

    if (!enabled) {
      setCharIndex(fullText.length);
      return;
    }

    // If text completely changed (not an extension), reset animation
    if (!fullText.startsWith(prevText.slice(0, Math.min(prevText.length, fullText.length > 0 ? 1 : 0)))) {
      setCharIndex(0);
      isRunningRef.current = false;
    }
  }, [fullText, enabled]);

  // Animation loop - runs independently of text updates
  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isRunningRef.current = false;
      return;
    }

    // Start the animation loop if not already running
    const tick = (): void => {
      const text = fullTextRef.current;
      const targetLength = text.length;
      const current = charIndexRef.current;

      if (current < targetLength) {
        const char = text[current];
        const delay = getCharDelay(char, current, targetLength);
        setCharIndex(current + 1);
        timeoutRef.current = setTimeout(tick, delay);
      } else if (current === targetLength && targetLength > 0) {
        // Caught up - check again soon in case more text arrives (streaming)
        timeoutRef.current = setTimeout(tick, 3);
      } else {
        // Done or no text
        isRunningRef.current = false;
        timeoutRef.current = null;
      }
    };

    // Start if not running and there's text to animate
    if (!isRunningRef.current && fullTextRef.current.length > 0) {
      isRunningRef.current = true;
      const text = fullTextRef.current;
      const targetLength = text.length;
      const safeIndex = Math.min(
        charIndexRef.current,
        Math.max(0, targetLength - 1),
      );
      const char = text[safeIndex];
      const delay = getCharDelay(char, safeIndex, targetLength);
      timeoutRef.current = setTimeout(tick, delay);
    }

    // Don't cleanup - let the animation continue running
  }, [enabled, fullText.length, charIndex]); // Only restart if enabled changes or text appears

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isRunningRef.current = false;
    setCharIndex(0);
    prevTextRef.current = '';
  }, []);

  const displayedText = enabled ? fullText.slice(0, charIndex) : fullText;
  const isComplete = !enabled || (charIndex >= fullText.length && fullText.length > 0);

  return {
    displayedText,
    isComplete,
    reset,
  };
}
